import React, { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Swiper as SwiperClass } from 'swiper'
import { Fancybox } from '@fancyapps/ui'

import 'swiper/css'
import 'swiper/css/free-mode'
import 'swiper/css/scrollbar'
import 'swiper/css/thumbs'
import '@fancyapps/ui/dist/fancybox/fancybox.css'

import { FreeMode, Navigation, Scrollbar, Thumbs } from 'swiper/modules'
import { ChevronNextIcon, ChevronPrevIcon, PhotoIcon, SearchIcon } from '@svgs'

interface IImageGallery {
  images?: string[] // Массив URL изображений из API
}

// 44px round arrows over the photo on a desktop; a phone swipes
const ARROW =
  'hidden md:flex absolute top-1/2 -translate-y-1/2 z-20 w-11 h-11 items-center justify-center rounded-full bg-surface/90 fill-ink shadow-pop hover:bg-surface transition-colors duration-200'

const ImageGallery: FC<IImageGallery> = ({ images }) => {
  const { t } = useTranslation()
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperClass | null>(null)
  const [current, setCurrent] = useState(0)
  const thumbnails = images ?? []

  // Bound once — rebinding on every render leaked handlers
  useEffect(() => {
    Fancybox.bind('[data-fancybox="gallery"]')
    return () => Fancybox.unbind('[data-fancybox="gallery"]')
  }, [])

  // A listing without photos used to show eight stock photos of somebody
  // else's flat — the most misleading thing a listing page can do.
  if (thumbnails.length === 0) {
    return (
      <div
        data-listing-gallery
        className="flex flex-col items-center justify-center gap-3 h-[16.875rem] md:h-[32.4375rem] rounded-2xl bg-brand-soft text-ink-2"
        data-testid="gallery-empty"
      >
        <span className="icon w-12 h-12 fill-brand opacity-40">
          <PhotoIcon />
        </span>
        <span className="text-base font-bold">{t('no_images')}</span>
      </div>
    )
  }

  return (
    <div data-listing-gallery className="flex md:flex-row flex-col gap-2 md:gap-4">
      {/* min-w-0/max-w-full are mandatory: a Swiper as a flex item without
          them blows up to a ~33M px track on phones (39.2) */}
      <Swiper
        navigation={{
          nextEl: '.gallery-swiper-button-next',
          prevEl: '.gallery-swiper-button-prev',
        }}
        loop={true}
        spaceBetween={10}
        speed={700}
        thumbs={thumbsSwiper ? { swiper: thumbsSwiper } : undefined}
        onSlideChange={(swiper) => setCurrent(swiper.realIndex)}
        modules={[FreeMode, Navigation, Thumbs]}
        className="flex-1 w-full min-w-0 max-w-full m-0 relative rounded-2xl overflow-hidden"
      >
        {thumbnails.map((slide, index) => (
          <SwiperSlide className="relative group !h-[16.875rem] md:!h-[32.4375rem]" key={index}>
            <a href={slide} data-fancybox="gallery">
              <img className="object-cover size-full" src={slide} alt="" />
            </a>

            <div className="absolute inset-0 z-10 bg-inverse/50 hidden md:flex flex-col items-center justify-center p-5 pointer-events-none group-hover:opacity-100 duration-300 transition-all opacity-0">
              <div className="flex flex-col items-center text-center gap-3">
                <div className="icon-button fill-white w-8 h-8">
                  <SearchIcon />
                </div>
                <div className="text-center text-white text-sm font-bold">
                  {t('open_in_full_screen')}
                </div>
              </div>
            </div>
          </SwiperSlide>
        ))}
        <button
          type="button"
          aria-label={t('gallery.prev')}
          className={`${ARROW} left-4 gallery-swiper-button-prev`}
        >
          <span className="icon-button w-4 h-4">
            <ChevronPrevIcon />
          </span>
        </button>
        <button
          type="button"
          aria-label={t('gallery.next')}
          className={`${ARROW} right-4 gallery-swiper-button-next`}
        >
          <span className="icon-button w-4 h-4">
            <ChevronNextIcon />
          </span>
        </button>
        {/* which photo of how many — phones have no thumbnails column */}
        {thumbnails.length > 1 && (
          <span
            data-gallery-counter
            aria-label={t('gallery.position', { current: current + 1, total: thumbnails.length })}
            className="absolute bottom-3 right-3 z-20 px-2.5 py-1 rounded-full bg-inverse/75 text-white text-xs font-bold leading-4 pointer-events-none"
          >
            {current + 1} / {thumbnails.length}
          </span>
        )}
      </Swiper>
      {/* thumbnails: a 56px strip under the photo on a phone (a thumbnail
          used to grow to the full width there), a column beside it on a desktop */}
      <Swiper
        data-gallery-thumbs
        scrollbar
        onSwiper={setThumbsSwiper}
        speed={700}
        spaceBetween={8}
        slidesPerView={'auto'}
        freeMode={true}
        watchSlidesProgress={true}
        modules={[FreeMode, Navigation, Thumbs, Scrollbar]}
        breakpoints={{
          992: {
            direction: 'vertical',
            spaceBetween: 12,
          },
        }}
        className="w-full m-0 md:h-[32.4375rem] md:!w-[7.0625rem] md:pr-3 swiper-right-scrollbar max-md:[&_.swiper-scrollbar]:hidden"
      >
        {thumbnails.map((slide, index) => (
          <SwiperSlide
            className="!w-[4.5rem] !h-14 md:!w-full md:!h-[4.5rem] rounded-xl overflow-hidden cursor-pointer opacity-60 transition-opacity [&.swiper-slide-thumb-active]:opacity-100 [&.swiper-slide-thumb-active]:outline [&.swiper-slide-thumb-active]:outline-2 [&.swiper-slide-thumb-active]:-outline-offset-2 [&.swiper-slide-thumb-active]:outline-brand"
            key={index}
          >
            <img className="object-cover size-full" src={slide} alt="" />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  )
}

export default ImageGallery
